import { sessionKeys } from '@taler/api-client'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { problemDetail } from '../internal/problem.ts'

export interface UseDefaultPaymentAccountOptions {
  readonly ledgerId: () => string | undefined
}

export interface UseDefaultPaymentAccount {
  readonly isSetting: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly errorDetail: ComputedRef<string | undefined>
  set(accountId: string): Promise<void>
}

/** The split-expense screen's one payment account, per (user, ledger) - myWealth §4.2. */
export function useDefaultPaymentAccount(options: UseDefaultPaymentAccountOptions): UseDefaultPaymentAccount {
  const api = useApi()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (accountId: string) => api.session.setDefaultPaymentAccount(options.ledgerId()!, accountId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.ledgers() })
    },
  })

  return {
    isSetting: computed(() => mutation.isPending.value),
    error: computed(() => mutation.error.value),
    errorDetail: computed(() => problemDetail(mutation.error.value)),
    set: (accountId) => mutation.mutateAsync(accountId).then(() => undefined),
  }
}
